<template>
  <div class="employee-dashboard">
    <!-- Header -->
    <header class="pb-4 pt-6 sm:pb-6">
      <div class="flex flex-wrap items-baseline justify-between gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
        <h1 class="text-base font-semibold leading-7">
          <BaseSelect
            v-model="selectedStat"
            :options="[
              { label: 'Cashflow', value: 'cashflow' },
              { label: 'Opportunities', value: 'opportunities' },
            ]"
          />
        </h1>
        <RadioGroup v-model="statsDateRange" class="order-last flex w-full gap-x-8 text-sm font-semibold leading-6 sm:order-none sm:w-auto sm:border-l sm:border-slate-300 sm:pl-6 sm:leading-7">
          <RadioGroupLabel class="sr-only">Date Range Filter</RadioGroupLabel>
          <RadioGroupOption v-slot="{ checked }" :value="dateRangeConstants.this_month" class="cursor-pointer capitalize">
            <span :class="checked ? 'text-accent' : 'text-base-300'">This month</span>
          </RadioGroupOption>

          <RadioGroupOption v-slot="{ checked }" :value="dateRangeConstants.this_quarter" class="cursor-pointer capitalize">
            <span :class="checked ? 'text-accent' : 'text-base-300'">This quarter</span>
          </RadioGroupOption>

          <RadioGroupOption v-slot="{ checked }" :value="dateRangeConstants.this_year" class="cursor-pointer capitalize">
            <span :class="checked ? 'text-accent' : 'text-base-300'">YTD</span>
          </RadioGroupOption>
        </RadioGroup>
        <BaseButton class="ml-auto" variant="accent" icon-class="i-heroicons-plus" icon-position="left" :to="{ name: 'CreateProject'}">
          New Project
        </BaseButton>
      </div>
    </header>

    <!-- Stats -->
    <OpportunitiesStats v-if="selectedStat === 'opportunities'" :stats-date-range="statsDateRange" />
    <CashflowStats v-else :stats-date-range="statsDateRange" />

    <!-- Recent projects & messages -->
    <div class="mx-auto mt-16">
      <div class="mx-auto grid max-w-7xl grid-cols-1 grid-rows-1 items-start gap-x-8 gap-y-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
        <!-- projects -->
        <div class="lg:col-span-2 lg:row-span-2 lg:row-end-2">
          <h2 class="text-base font-semibold leading-6">Projects</h2>
          <AppTabs v-if="!!isLoading" :items="projectTabs">
            <template #inProgress>
              <ProjectsList v-if="inProgressProjects?.length > 0"  :items="inProgressProjects" />
              <EmptyState v-else-if="inProgressProjects?.length === 0" />
            </template>
            <template #awaiting>
              <ProjectsList v-if="awaitingProjects?.length > 0" :items="awaitingProjects" />
              <EmptyState v-else />
            </template>
            <template #estimating>
              <ProjectsList v-if="estimatingProjects?.length > 0" :items="estimatingProjects" />
              <EmptyState v-else />
            </template>
            <template #completed>
              <ProjectsList v-if="completedProjects?.length > 0" :items="completedProjects" />
              <EmptyState v-else />
            </template>
          </AppTabs>
        </div>

        <!-- messages -->
        <div class="lg:col-start-3">
          <h2 class="text-base font-semibold leading-6">Activities</h2>
          <ul v-if="!!isLoading" role="list" class="divide-y divide-slate-200">
            <li v-for="(messages, projectID) in groupedProjectMessages" :key="projectID" class="px-4 py-4 sm:px-0">
              <div class="items">
                <div class="flex flex-col">
                  <a href="#" class="text-sm font-medium flex justify-between">
                    <span>{{ messages[0].project.name }}</span>
                    <span class="i-heroicons-chevron-right size-5" />
                  </a>
                  
                   <!-- list messages -->
                  <ul role="list" class="divide-y divide-slate-200">
                    <li v-for="message in messages" :key="message.id" class="px-4 py-4 sm:px-0">
                      <div class="items overflow-hidden">
                        <div class="flex flex-col">
                          <div class="flex gap-2">
                            <span class="text-xs font-medium">{{ message.from.name }}</span>
                            <span class="text-xs text-slate-500">{{ useTimeAgo(new Date(message.created)) }}</span>
                          </div>
                          <span class="text-xs font-normal text-wrap text-ellipsis overflow-hidden" v-html="renderLinks(message.text )" />
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </li>
          </ul>
        </div>

      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, Ref } from 'vue'
import AppTabs, { TabItem } from '@/components/app/AppTabs.vue'
// import AppActivityFeed from '@/components/app/AppActivityFeed.vue'
import ProjectsList from '@/components/project/ProjectsList.vue'
import EmptyState from '@/components/app/EmptyState.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { useQuery } from '@tanstack/vue-query'
import ProjectService from '@/services/ProjectService'
import { PROJECT_STATUS } from '@/models/project'
import { eq, orderByDesc, dateRangeConstants, dateRangeFilter } from '@/utils/queryFilters'
import { renderLinks } from '@/utils/'
import {
  RadioGroup,
  RadioGroupLabel,
  RadioGroupOption,
} from '@headlessui/vue'
import MessageService from '@/services/MessageService'
import { useTimeAgo } from '@vueuse/core'
import BaseSelect from '@/components/base/BaseSelect.vue'
import CashflowStats from './CashflowStats.vue'
import OpportunitiesStats from './OpportunitiesStats.vue'

const selectedStat = ref('cashflow')
const statsDateRange = ref(dateRangeConstants.this_month)
const projectService = new ProjectService()

const { data: estimatingProjects, isPending: estimatingPending } = useQuery({
  queryKey:['projects', PROJECT_STATUS.ESTIMATING, statsDateRange],
  queryFn: async () => {
    const data = await projectService.where(
      [eq('status', PROJECT_STATUS.ESTIMATING), dateRangeFilter('updatedAt', statsDateRange.value)],
      [orderByDesc('updatedAt')],
      5
    )
    return data
  }
})
const { data: awaitingProjects, isPending: awaitingPending } = useQuery({
  queryKey:['projects', PROJECT_STATUS.AWAITING_ACCEPTANCE, statsDateRange],
  queryFn: async () => {
    const data = await projectService.where(
      [eq('status', PROJECT_STATUS.AWAITING_ACCEPTANCE), dateRangeFilter('updatedAt', statsDateRange.value)],
      [orderByDesc('updatedAt')],
      5
    )
    return data
  }
})
const { data: inProgressProjects, isPending: inProgressPending } = useQuery({
  queryKey:['projects', PROJECT_STATUS.IN_PROGRESS, statsDateRange],
  queryFn: async () => {
    const data = await projectService.where(
      [eq('status', PROJECT_STATUS.IN_PROGRESS), dateRangeFilter('updatedAt', statsDateRange.value)],
      [orderByDesc('updatedAt')],
      5
    )
    return data
  }
})
const { data: completedProjects, isPending: completedPending } = useQuery({
  queryKey:['projects', PROJECT_STATUS.COMPLETED, statsDateRange],
  queryFn: async () => {
    const data = await projectService.where(
      [eq('status', PROJECT_STATUS.COMPLETED), dateRangeFilter('updatedAt', statsDateRange.value)],
      [orderByDesc('updatedAt')],
      5
    )
    return data
  }
})

const { data: groupedProjectMessages, isPending: groupedProjectMessagesPending } = useQuery({
  queryKey: ['recent-messages',],
  queryFn: async () => {
    const messageService = new MessageService('')
    const data = await messageService.getRecentMessages({
      daysAgo: 90,
      group: true,
      limit: 5
    })
    return data
  }
})

const projectTabs: Ref<TabItem[]> = ref([
  { label: 'In construction', slot: 'inProgress' },
  { label: 'Proposal sent', slot: 'awaiting' },
  { label: 'Budgeting', slot: 'estimating' },
  { label: 'Completed invoiced', slot: 'completed' },
])

const isLoading = computed(() => estimatingPending || awaitingPending || inProgressPending || completedPending || groupedProjectMessagesPending)
</script>
